import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Apollo, gql } from 'apollo-angular';
import { FetchResult, ApolloQueryResult } from '@apollo/client/core';
import { User } from '../models/user';
import { AuthorizeUser } from '../results/user/authorize-user';
import { UserStorageService } from '@app/core/services/user-storage.service';
import { UserResult } from '../results/user/user-result';
import { RequestUserPasswordResetResult } from '../results/user/request-user-password-reset-result';
import { ResetUserPasswordResult } from '../results/user/reset-user-password-result';
import { AuthorizedUser } from '../models/authorized-user';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private loggedIn = new BehaviorSubject<AuthorizedUser | null>(this.userStorageService.getCurrentUser());

  constructor(
    private apollo: Apollo,
    private userStorageService: UserStorageService
  ) {}

  login(email: string, password: string): Observable<FetchResult<AuthorizeUser>> {
    return this.apollo.mutate<AuthorizeUser>({
      mutation: gql`
           mutation authorizeUser($email: String!, $password: String!) {
           authorizeUser(email: $email, password: $password) {
            accessToken
            refreshToken,
            user {
              id
              firstName
              lastName
              fullName
              email
              emailHash
              phone
              lastLogin
              supervisor {
                id
                fullName
              }
              occupation {
                shiftHours
              }
              roles {
                id
                name
                permissions {
                  id
                  identifier
                }
              }
              city{
                id
                name
                externalIdentifier
              }
              subordinates {
                edges {
                  node {
                    id
                    fullName
                    city {
                      externalIdentifier
                    }
                  }
                }
              }
            }
          }
          }
          `,
      variables: {
        email: email,
        password: password
      },
    }).pipe(map((result: FetchResult<AuthorizeUser>): FetchResult<AuthorizeUser> => {
      // login successful if there's a jwt token in the response
      if (result.data?.authorizeUser?.accessToken) {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        this.userStorageService.setCurrentUser(result.data.authorizeUser);
        this.loggedIn.next(result.data.authorizeUser);
      }
      return result;
    }));
  }

  refresh(): Observable<FetchResult<AuthorizeUser>> {
    return this.apollo.mutate<AuthorizeUser>({
      mutation: gql`
           mutation refreshUserToken($refreshToken: String!) {
            refreshUserToken(refreshToken: $refreshToken) {
            accessToken
            refreshToken,
            user {
              id
              firstName
              lastName
              fullName
              email
              emailHash
              phone
              lastLogin
              supervisor {
                id
                fullName
              }
              occupation {
                shiftHours
              }
              roles {
                id
                name
                permissions {
                  id
                  identifier
                }
              }
            }
          }
          }
          `,
      variables: {
        refreshToken: this.userStorageService.getRefreshToken(),
      },
    }).pipe(map((result: FetchResult<AuthorizeUser>): FetchResult<AuthorizeUser> => {
      // login successful if there's a jwt token in the response
      if (result.data?.authorizeUser?.accessToken) {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        this.userStorageService.setCurrentUser(result.data.authorizeUser);
        this.loggedIn.next(result.data.authorizeUser);
      }
      return result;
    }));
  }

  requestPasswordReset(email: string, emailSubject: string, emailContent: string): Observable<FetchResult<RequestUserPasswordResetResult>> {
    return this.apollo.mutate({
      mutation: gql`
           mutation requestUserPasswordReset($email: String!, $emailSubject: String!, $emailContent: String!) {
            requestUserPasswordReset(email: $email, emailSubject: $emailSubject, emailContent: $emailContent) {
              message
              taskId
            }
          }
          `,
      variables: {
        email: email,
        emailSubject: emailSubject,
        emailContent: emailContent
      },
    });
  }

  doPasswordReset(resetToken: string, newPassword: string): Observable<FetchResult<ResetUserPasswordResult>> {
    return this.apollo.mutate({
      mutation: gql`
           mutation resetUserPassword($resetToken: String!, $newPassword: String!) {
            resetUserPassword(resetToken: $resetToken, newPassword: $newPassword) {
              user {
                id
                firstName
                lastName
                fullName
                email
                emailHash
                phone
                lastLogin
                supervisor {
                  id
                  fullName
                }
                occupation {
                  shiftHours
                }
                roles {
                  id
                  name
                  permissions {
                    id
                    identifier
                  }
                }
              }
            }
          }
          `,
      variables: {
        resetToken: resetToken,
        newPassword: newPassword,
      },
    });
  }

  updatePassword(password: string, newPassword: string): Observable<FetchResult<AuthorizeUser>> {
    return this.apollo.mutate({
      mutation: gql`
           mutation updateAuthorizedUserPassword($password: String!, $newPassword: String!) {
            updateAuthorizedUserPassword(password: $password, newPassword: $newPassword) {
              user {
                id
                firstName
                lastName
                fullName
                email
                emailHash
                phone
                lastLogin
                supervisor {
                  id
                  fullName
                }
                occupation {
                  shiftHours
                }
                roles {
                  id
                  name
                  permissions {
                    id
                    identifier
                  }
                }
              }
            }
          }
          `,
      variables: {
        password: password,
        newPassword: newPassword,
      },
    });
  }


  logOut(): void {
    // remove user from local storage to log user out
    this.loggedIn.next(null);
    this.userStorageService.logOut();
  }

  register(user: User): Observable<FetchResult<UserResult>> {
    return this.apollo.mutate({
      mutation: gql`
           mutation registerUser($user: String!) {
            registerUser(user: $user) {
            accessToken
            refreshToken,
            user {
              id
              firstName
              lastName
              fullName
              email
              emailHash
              phone
              lastLogin
              supervisor {
                id
                fullName
              }
              occupation {
                shiftHours
              }
              roles {
                id
                name
                permissions {
                  id
                  identifier
                }
              }
            }
          }
          }
          `,
      variables: {
        user: user,
      },
    });
  }

  updateUser(user: User): Observable<FetchResult<UserResult>> {
    return this.apollo.mutate({
      mutation: gql`
           mutation updateAuthorizedUser($user: UserInput!) {
            updateAuthorizedUser(user: $user) {
              user {
                id
                firstName
                lastName
                fullName
                email
                emailHash
                phone
                lastLogin
                supervisor {
                  id
                  fullName
                }
                occupation {
                  shiftHours
                }
                roles {
                  id
                  name
                  permissions {
                    id
                    identifier
                  }
                }
              }
            }
          }
          `,
      variables: {
        user: user,
      },
    });
  }

  getUser(): Observable<ApolloQueryResult<UserResult>> {
    return this.apollo.query<UserResult>({
      query: gql`
      query authorizedUser {
        user: authorizedUser {
          id
          firstName
          lastName
          fullName
          email
          emailHash
          phone
          lastLogin
          supervisor {
            id
            fullName
          }
          occupation {
            shiftHours
          }
          roles {
            id
            name
            permissions {
              id
              identifier
            }
          }
        }
      }
     `
    });
  }

  metamorphose(user: User): Observable<FetchResult<AuthorizeUser>> {
    return this.apollo.mutate<AuthorizeUser>({
      mutation: gql`
           mutation metamorphoseUser($assimilatedUser: UserInput!) {
            authorizeUser: metamorphoseUser(assimilatedUser: $assimilatedUser) {
            accessToken
            refreshToken,
            user {
              id
              firstName
              lastName
              fullName
              email
              emailHash
              phone
              lastLogin
              supervisor {
                id
                fullName
              }
              occupation {
                shiftHours
              }
              roles {
                id
                name
                permissions {
                  id
                  identifier
                }
              }
            }
          }
          }
          `,
      variables: {
        assimilatedUser: user,
      },
    }).pipe(map((result: FetchResult<AuthorizeUser>): FetchResult<AuthorizeUser> => {
      // login successful if there's a jwt token in the response
      if (result.data?.authorizeUser?.accessToken) {
        // store user details and jwt token in local storage to keep user logged in between page refreshes
        this.userStorageService.setCurrentUser(result.data.authorizeUser);
        this.loggedIn.next(result.data.authorizeUser);
      }
      return result;
    }));
  }

  get isLoggedIn(): Observable<AuthorizedUser | null> {
    return this.loggedIn.asObservable();
  }
}
